<template>
    <div class="row">
        <div class="col-md-8 col-lg-6">

            <ui-input-box-floating class="mb-3">
                <select class="form-control form-select" v-model="selectedKey" :disabled="disabled">
                    <option value="">Не выбран</option>
                    <option v-for="op in operators" :key="opKey(op)" :value="opKey(op)">
                        {{ op.title || op.name }} (ИНН {{ op.inn }})
                    </option>
                </select>
                <label>Оператор ЭДО</label>
            </ui-input-box-floating>

            <div v-if="!foundInEdo" class="form-group">
                <label>
                    <input v-model="props.data.checkboxEdoRequest" type="checkbox" name="checkboxEdoRequest"
                        :disabled="disabled">
                    <span>Отправить приглашение на подключение к ЭДО</span>
                </label>
            </div>

            <template v-if="showIdField">
                <ui-input-box-floating class="mb-1 mt-2" :error="displayError">
                    <ui-input v-model="props.data.EdoParticipantId" type="text"
                        placeholder="Идентификатор участника ЭДО" :disabled="disabled"
                        @blur="idTouched = true" />
                    <ui-input-label>Идентификатор участника ЭДО</ui-input-label>
                </ui-input-box-floating>
                <div v-if="displayError" class="text-danger small mb-3">{{ displayError }}</div>
                <div v-else class="text-muted small mb-3">
                    Идентификатор должен начинаться с {{ hintPrefixes }} (первые 3 символа —
                    код оператора ЭДО).
                </div>
            </template>

        </div>
    </div>
</template>


<script setup>

import { ref, computed, onMounted, watch } from 'vue'

const props = defineProps({
    data: {
        type: Object,
        default: {}
    },
    errors: {
        type: Object,
        default: {}
    },
    disabled: {
        type: Boolean,
        default: false
    }
})

// Список по умолчанию на случай, если анкета сохранена без EdoOperators
// или список из API неполный
const defaultOperators = [
    {
        title: 'Saby / СБИС',
        name: 'ООО "Компания "Тензор"',
        inn: '7605016030',
        kpp: '',
    },
    {
        title: 'Контур.Диадок',
        name: 'АО "ПФ "СКБ Контур"',
        inn: '6663003127',
        kpp: '668601001',
    },
    {
        title: 'Астрал.ЭДО',
        name: 'АО "Калуга Астрал"',
        inn: '4029017981',
        kpp: '',
    },
    {
        title: 'Сбер2B ЭДО / СберКорус',
        name: 'ООО "СберКорус"',
        inn: '7801392271',
        kpp: '',
    },
    {
        title: 'Такском',
        name: 'ООО "Такском"',
        inn: '7704211201',
        kpp: '',
    },
]

const operators = computed(() => {
    const fromApi = Array.isArray(props.data.EdoOperators) ? props.data.EdoOperators : []
    const missing = defaultOperators.filter(def => !fromApi.some(op => op.inn === def.inn))
    return [...fromApi, ...missing]
})

const opKey = (op) => `${op.inn}|${op.kpp || ''}`

// Префиксы идентификатора участника ЭДО по операторам (таблица операторов).
// Первые 3 символа идентификатора должны соответствовать выбранному оператору.
const OPERATOR_PREFIXES = {
    '7605016030': ['2BE'], // ООО «Компания Тензор» (Saby / СБИС)
    '6663003127': ['2BM'], // АО ПФ «СКБ Контур» (Контур.Диадок)
    '4029017981': ['2AE'], // АО «Калуга Астрал» (Астрал.ЭДО, Доки)
    '7801392271': ['2BK'], // ООО «СберКорус» (СФЕРА Курьер)
    '7704211201': ['2AL'], // ООО «Такском» (Такском-Файлер)
}

// Полный список известных префиксов (в т.ч. операторов без ИНН в справочнике).
const ALL_PREFIXES = ['2BE', '2BM', '2AE', '2BK', '2BA', '2CI', '2EE', '2AL', '2PS', '2VO', '2LJ']

const selectedKey = ref('')

// Оператор определен сервисом (контрагент уже в ЭДО) — есть uid.
// При ручном выборе uid = null, поэтому показываем чекбокс приглашения.
const foundInEdo = computed(() => !!props.data.EdoOperator?.uid)

// Поле доступно и обязательно только при запросе приглашения на подключение к ЭДО.
const showIdField = computed(() => !foundInEdo.value && !!props.data.checkboxEdoRequest)

// Ожидаемые префиксы для выбранного оператора (если он есть в справочнике).
const expectedPrefixes = computed(() => {
    const inn = props.data.EdoOperator?.inn
    return inn && OPERATOR_PREFIXES[inn] ? OPERATOR_PREFIXES[inn] : ALL_PREFIXES
})

const hintPrefixes = computed(() => expectedPrefixes.value.join(', '))

// Клиентская валидация идентификатора участника ЭДО.
const idError = computed(() => {
    if (!showIdField.value) return ''
    const id = (props.data.EdoParticipantId || '').trim()
    if (!id) return 'Укажите идентификатор участника ЭДО'
    const prefix = id.slice(0, 3).toUpperCase()
    if (!expectedPrefixes.value.includes(prefix)) {
        return `Идентификатор должен начинаться с ${hintPrefixes.value}`
    }
    return ''
})

// Красную подсветку показываем только после того, как поле трогали,
// и только пока значение некорректно.
const idTouched = ref(false)
const displayError = computed(() => (idTouched.value ? idError.value : ''))

onMounted(() => {
    const found = props.data.EdoOperator

    if (found && found.inn) {
        const match = operators.value.find(op =>
            op.inn === found.inn && (!found.kpp || !op.kpp || op.kpp === found.kpp)
        )
        if (match) {
            selectedKey.value = opKey(match)
        }
    }
})

watch(selectedKey, (key) => {
    const op = operators.value.find(o => opKey(o) === key)

    if (op) {
        const prev = props.data.EdoOperator
        props.data.EdoOperator = {
            title: op.title || '',
            name: op.name,
            inn: op.inn,
            kpp: op.kpp,
            uid: prev && prev.inn === op.inn ? prev.uid ?? null : null,
        }
    } else {
        props.data.EdoOperator = null
    }
})

</script>
